import { Alert, Box, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GooglePickerComponent, {
  FileData,
} from 'src/components/google-picker/googleFilePicker';
import { CHAT_FILE_ACCESS_MIME_TYPES } from 'src/constants/chatFileAccess';
import { verifyDriveFileAccess } from 'src/services/driveFileAccess';

interface Props {
  fileId: string | null;
  autoPick: boolean;
}
type VerificationState =
  | 'idle'
  | 'verifying'
  | 'verified'
  | 'failed'
  | 'mismatch';

/** Chat access is a user OAuth grant, separate from memory and file sharing. */
export function DriveFileAccessApproval({ fileId, autoPick }: Props) {
  const { t } = useTranslation();
  const [state, setState] = useState<VerificationState>('idle');
  const requestGeneration = useRef(0);
  const selectionLifecycle = useMemo(() => ({ active: false }), [fileId]);

  useEffect(() => {
    selectionLifecycle.active = true;
    requestGeneration.current += 1;
    setState('idle');

    // Ignore requests completed after navigating to a different file or page.
    return () => {
      selectionLifecycle.active = false;
      requestGeneration.current += 1;
    };
  }, [fileId, selectionLifecycle]);

  const verifySelection = async (files: FileData[]) => {
    // A previous Picker can deliver its first callback after navigation.
    // Reject that callback before it can start a new verification generation.
    if (!selectionLifecycle.active) return;
    const generation = ++requestGeneration.current;

    if (!fileId || files.length !== 1 || files[0].id !== fileId) {
      setState('mismatch');

      return;
    }

    setState('verifying');
    const result = await verifyDriveFileAccess(fileId);

    if (requestGeneration.current !== generation) return;
    setState(
      result.success && result.content?.fileId === fileId
        ? 'verified'
        : 'failed',
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5">{t('driveFileAccess.title')}</Typography>
      <Typography sx={{ my: 2 }}>{t('driveFileAccess.instruction')}</Typography>
      {!fileId ? (
        <Alert severity="error">{t('driveFileAccess.mismatch')}</Alert>
      ) : (
        <>
          <GooglePickerComponent
            key={fileId}
            autoPickFileId={fileId}
            autoPickEnabled={autoPick}
            multiSelectEnabled={false}
            selectableMimeTypes={CHAT_FILE_ACCESS_MIME_TYPES}
            onFilesSelected={verifySelection}
            onConsentRequired={() => {
              if (selectionLifecycle.active) setState('failed');
            }}
          />
          {state !== 'idle' && (
            <Alert
              sx={{ mt: 2 }}
              severity={
                state === 'verified'
                  ? 'success'
                  : state === 'verifying'
                    ? 'info'
                    : 'error'
              }
            >
              {t(`driveFileAccess.${state}`)}
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
